import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, CalendarOff, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

// Python weekday(): Mo=0 ... So=6
const WEEKDAYS = [
  { idx: 0, label: "Montag" }, { idx: 1, label: "Dienstag" }, { idx: 2, label: "Mittwoch" },
  { idx: 3, label: "Donnerstag" }, { idx: 4, label: "Freitag" }, { idx: 5, label: "Samstag" }, { idx: 6, label: "Sonntag" },
];

function fmtDate(d) { try { return new Date(d).toLocaleDateString("de-DE"); } catch { return d; } }

export default function EinstellungenPage() {
  const [restDays, setRestDays] = useState([1]);
  const [closures, setClosures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingDays, setSavingDays] = useState(false);
  const [form, setForm] = useState({ date_from: "", date_to: "", label: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/settings");
    setRestDays(data.rest_days || []); setClosures(data.closures || []); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggleDay = (idx) => setRestDays((d) => d.includes(idx) ? d.filter((x) => x !== idx) : [...d, idx]);

  const saveDays = async () => {
    setSavingDays(true);
    try { await api.put("/settings", { rest_days: restDays }); toast.success("Ruhetage gespeichert"); }
    catch { toast.error("Fehler"); } finally { setSavingDays(false); }
  };

  const addClosure = async () => {
    if (!form.date_from || !form.date_to) { toast.error("Von- und Bis-Datum wählen"); return; }
    if (form.date_to < form.date_from) { toast.error("Bis-Datum liegt vor Von-Datum"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/settings/closures", form);
      setClosures((c) => [...c, data]);
      setForm({ date_from: "", date_to: "", label: "" });
      toast.success("Schließtage hinzugefügt");
    } catch { toast.error("Fehler"); } finally { setBusy(false); }
  };

  const delClosure = async (id) => { await api.delete(`/settings/closures/${id}`); setClosures((c) => c.filter((x) => x.id !== id)); };

  if (loading) return <div className="flex items-center gap-2 text-slate-500 py-12 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Laden …</div>;

  return (
    <div>
      <PageHeader title="Öffnungszeiten & Schließtage" subtitle="Ruhetage und Betriebsferien – an geschlossenen Tagen sind keine Aufgaben fällig" />

      {/* Ruhetage */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 mb-6 max-w-2xl">
        <h3 className="font-heading font-bold text-slate-800 mb-1">Wöchentliche Ruhetage</h3>
        <p className="text-sm text-slate-500 mb-4">Wähle die Tage, an denen der Betrieb regulär geschlossen ist.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          {WEEKDAYS.map((w) => (
            <label key={w.idx} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition-colors ${restDays.includes(w.idx) ? "border-teal-400 bg-teal-50 text-teal-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              <Checkbox checked={restDays.includes(w.idx)} onCheckedChange={() => toggleDay(w.idx)} data-testid={`restday-${w.idx}`} />
              {w.label}
            </label>
          ))}
        </div>
        <Button onClick={saveDays} disabled={savingDays} data-testid="btn-save-restdays">
          {savingDays ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Ruhetage speichern
        </Button>
      </div>

      {/* Schließtage / Betriebsferien */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 max-w-2xl">
        <h3 className="font-heading font-bold text-slate-800 mb-1">Betriebsferien & Schließtage</h3>
        <p className="text-sm text-slate-500 mb-4">Einzelne Feiertage oder ganze Ferien-Zeiträume – in diesem Zeitraum sind keine Aufgaben fällig.</p>

        <div className="grid sm:grid-cols-4 gap-3 items-end mb-4">
          <div className="space-y-1"><Label className="text-xs">Von</Label>
            <Input type="date" data-testid="closure-from" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })} /></div>
          <div className="space-y-1"><Label className="text-xs">Bis</Label>
            <Input type="date" data-testid="closure-to" value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })} /></div>
          <div className="space-y-1"><Label className="text-xs">Bezeichnung</Label>
            <Input data-testid="closure-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="z. B. Sommerferien" /></div>
          <Button onClick={addClosure} disabled={busy} data-testid="btn-add-closure">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />} Hinzufügen
          </Button>
        </div>

        {closures.length === 0 ? (
          <div className="text-center py-8 text-slate-400 border border-dashed border-slate-200 rounded-lg"><CalendarOff className="h-8 w-8 mx-auto mb-1" />Keine Schließtage eingetragen</div>
        ) : (
          <div className="space-y-2">
            {closures.slice().sort((a, b) => a.date_from.localeCompare(b.date_from)).map((c) => (
              <div key={c.id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-4 py-2.5" data-testid="closure-row">
                <CalendarOff className="h-4 w-4 text-amber-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800">{c.label || "Schließtage"}</div>
                  <div className="text-xs text-slate-500">{fmtDate(c.date_from)} – {fmtDate(c.date_to)}</div>
                </div>
                <StatusBadge variant="due">geschlossen</StatusBadge>
                <button onClick={() => delClosure(c.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
